import React, { useRef } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { Box } from '@atlaskit/primitives';
import {
	useEpicIssueType,
	useEpicIssueTypeName,
} from '@atlassian/jira-backlog-epics-store/src/selectors/index.tsx';
import { VISIBLE } from '@atlassian/jira-platform-inline-card-create/src/common/constants.tsx';
import { ScrollStateContextProvider } from '@atlassian/jira-software-fast-virtual/src/services/use-scroll-state/index.tsx';
import Panel from '../../../common/ui/panel/main.tsx';
import { CardOpenProvider } from '../../../context/use-card-open.tsx';
import { usePanelData } from '../../../context/use-panel-data.tsx';
import CreateIssueButton from './create-issue-button/index.tsx';
import { EpicCardList } from './epic-card-list/index.tsx';
import EpicInlineCardCreate from './epic-inline-card-create/main.tsx';
import type { CreateEpicButtonProps, EpicPanelProps } from './types.tsx';

export const CreateEpicButton = ({
	isCreateEnabled,
	hierarchyName,
	onCreateEpic,
	onCreateCMPEpic,
}: CreateEpicButtonProps) => {
	const { isCompanyManaged } = usePanelData();
	const [epicIssueType] = useEpicIssueType();

	if (!isCreateEnabled) {
		return null;
	}

	return (
		<Box paddingInline="space.050">
			{isCompanyManaged ? (
				<CreateIssueButton
					onCreate={onCreateCMPEpic}
					issueType={epicIssueType}
					hierarchyName={hierarchyName}
				/>
			) : (
				<EpicInlineCardCreate
					epicIssueType={epicIssueType}
					triggerAppearance={VISIBLE}
					onCreate={onCreateEpic}
				/>
			)}
		</Box>
	);
};

/**
 * This is the main entrypoint for the epic panel, responsible for rendering the actual list of epic cards
 */
const EpicPanel = ({
	isCreateEnabled,
	isEditEnabled,
	selectedIssues,
	hierarchyName,
	onClose = noop,
	onCardDetailClick = noop,
	onCreateIssueClick,
	onCreateEpic = noop,
	onCreateCMPEpic,
	onAssignEpic,
	onRefetch,
}: EpicPanelProps) => {
	const scrollElementRef = useRef<HTMLDivElement | null>(null);
	const [epicIssueTypeName] = useEpicIssueTypeName();

	return (
		<Panel ref={scrollElementRef} title={epicIssueTypeName} onClose={onClose}>
			<DroppableArea>
				<ScrollStateContextProvider scrollRef={scrollElementRef}>
					<CardOpenProvider>
						<EpicCardList
							isEditEnabled={isEditEnabled}
							selectedIssues={selectedIssues}
							onAssignEpic={onAssignEpic}
							onCardDetailClick={onCardDetailClick}
							onCreateIssueClick={onCreateIssueClick}
							onRefetch={onRefetch}
						/>
					</CardOpenProvider>
					<CreateEpicButton
						isCreateEnabled={isCreateEnabled}
						hierarchyName={hierarchyName}
						onCreateEpic={onCreateEpic}
						onCreateCMPEpic={onCreateCMPEpic}
					/>
				</ScrollStateContextProvider>
			</DroppableArea>
		</Panel>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DroppableArea = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 0,
});

export default EpicPanel;
